import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";

const EMPTY = { title: "Lesson quiz", pass_mark_pct: 70, questions: [] };

const QuestionCard = ({ q, idx, update, remove }) => (
  <div className="border border-border rounded p-3 bg-slate-50/50" data-testid={`quiz-question-${idx}`}>
    <div className="flex items-center justify-between mb-2">
      <Badge variant="outline" className="text-[10px] uppercase">{q.kind}</Badge>
      <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => remove(idx)} data-testid={`quiz-question-remove-${idx}`}><Trash2 size={11} /></Button>
    </div>
    <Label className="text-xs">Prompt</Label>
    <Textarea value={q.prompt} onChange={(e) => update(idx, { prompt: e.target.value })} className="min-h-[60px]" data-testid={`quiz-question-prompt-${idx}`} />
    {q.kind === "mcq" ? (
      <div className="mt-2 space-y-1">
        {q.options.map((opt, oi) => (
          <div key={oi} className="flex items-center gap-2">
            <input type="radio" checked={q.correct_index === oi} onChange={() => update(idx, { correct_index: oi })} />
            <Input value={opt} onChange={(e) => update(idx, { options: q.options.map((o, j) => (j === oi ? e.target.value : o)) })} placeholder={`Option ${oi + 1}`} />
          </div>
        ))}
      </div>
    ) : (
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Min words</Label><Input type="number" value={q.min_words} onChange={(e) => update(idx, { min_words: parseInt(e.target.value) || 0 })} /></div>
        <div><Label className="text-xs">Points</Label><Input type="number" value={q.points} onChange={(e) => update(idx, { points: parseInt(e.target.value) || 1 })} /></div>
      </div>
    )}
  </div>
);

export const QuizDialog = ({ lessonId, onClose }) => {
  const [draft, setDraft] = useState(EMPTY);

  useEffect(() => {
    if (!lessonId) return;
    api.get(`/lms/admin/lessons/${lessonId}/quiz`)
      .then(({ data }) => setDraft({ title: data.title, pass_mark_pct: data.pass_mark_pct, questions: data.questions || [] }))
      .catch(() => setDraft(EMPTY));
  }, [lessonId]);

  const add = (kind) => setDraft((p) => ({ ...p, questions: [...p.questions, kind === "mcq"
    ? { kind: "mcq", prompt: "", options: ["", "", "", ""], correct_index: 0, points: 1 }
    : { kind: "written", prompt: "", min_words: 50, points: 2 }] }));
  const update = (idx, patch) => setDraft((p) => ({ ...p, questions: p.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) }));
  const remove = (idx) => setDraft((p) => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));

  const save = async () => {
    try {
      await api.put(`/lms/admin/lessons/${lessonId}/quiz`, { ...draft, lesson_id: lessonId });
      toast.success("Quiz saved"); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <Dialog open={!!lessonId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quiz-dialog">
        <DialogHeader><DialogTitle>Lesson quiz</DialogTitle><DialogDescription className="text-xs">Multiple-choice and written questions for this lesson.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label className="text-xs">Quiz title</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="quiz-title" /></div>
            <div><Label className="text-xs">Pass mark %</Label><Input type="number" min="0" max="100" value={draft.pass_mark_pct} onChange={(e) => setDraft({ ...draft, pass_mark_pct: parseInt(e.target.value) || 0 })} data-testid="quiz-pass-mark" /></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => add("mcq")} data-testid="quiz-add-mcq">+ MCQ</Button>
            <Button size="sm" variant="outline" onClick={() => add("written")} data-testid="quiz-add-written">+ Written</Button>
          </div>
          {draft.questions.map((q, idx) => <QuestionCard key={idx} q={q} idx={idx} update={update} remove={remove} />)}
          {draft.questions.length === 0 && <p className="text-sm text-muted-foreground italic text-center py-4">Add some questions.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-green-700 hover:bg-green-800 text-white" onClick={save} data-testid="quiz-save">Save quiz</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
